import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import { getCellReducers } from './helpers/getCellReducers';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
  useDeleteSubscriptionRestriction,
  useGetRestrictions,
} from '../../../services/redux/api/subscriptionsApi';
import { ISubscriptionRestriction } from '../../../types/interfaces/subscription-restriction';
import { useAccess } from '../../../hooks/useAccess';
import { FormikProps, useFormik } from 'formik';
import { RestrictionScope, SubscriptionType } from '../../../shared/constants';

export function useSubscriptionRestrictionsComponent(classes) {
  const { t } = useTranslation();
  const { data: restrictions } = useGetRestrictions();
  const [itemForDelete, setItemForDelete] =
    useState<ISubscriptionRestriction>(null);
  const [itemForEditing, setItemForEditing] =
    useState<ISubscriptionRestriction>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteSubscriptionRestriction] = useDeleteSubscriptionRestriction();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: allOption,
      restrictionScope: allOption,
    },
    onSubmit: () => {},
  });
  const filteredValues = useMemo(
    () =>
      restrictions?.filter((restriction: ISubscriptionRestriction) => {
        const typeFilterId = filterFormik.values.type?.id ?? 'all';
        const restrictionScopeFilterId =
          filterFormik.values.restrictionScope?.id ?? 'all';
        const filterValue = filter.toLowerCase();
        if (
          !String(restriction.type ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(restriction.restrictionScope ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !restriction.maxValue?.toString().includes(filterValue)
        ) {
          return false;
        }

        if (typeFilterId !== 'all' && restriction.type !== typeFilterId)
          return false;

        if (
          restrictionScopeFilterId !== 'all' &&
          restriction.restrictionScope !== restrictionScopeFilterId
        )
          return false;

        return true;
      }),
    [restrictions, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.subscriptionRestrictions?.canEdit ?? false,
      canDelete: access?.subscriptionRestrictions?.canDelete ?? false,
    },
    (restriction: ISubscriptionRestriction) => {
      setItemForEditing(restriction);
      setShowEditModal(true);
    },
    (restriction: ISubscriptionRestriction) => {
      setItemForDelete(restriction);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.SUBSCRIPTION_RESTRICTION_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteSubscriptionRestriction({ id: itemForDelete.id });
  }

  const filterTypeOptions = [
    allOption,
    ...Object.values(SubscriptionType).map((type) => ({
      id: type,
      name: type,
    })),
  ];

  const filterRestrictionScopeOptions = [
    allOption,
    ...Object.values(RestrictionScope).map((scope) => ({
      id: scope,
      name: scope,
    })),
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterTypeOptions,
    filterRestrictionScopeOptions,
  };
}
